import React from 'react';
import Card from 'material-ui/Card';
import {Map, List} from 'immutable';

import SelectField from 'components/pure/utils/form/SelectField';
import MultipleSelectField from 'components/pure/utils/select-field/MultipleSelectField';

import Button from '@material-ui/core/Button';
import LibraryActions from 'actions/library/LibraryActions';
import ConstValues from 'utils/const';

/**
 * Function to be passed to sort that compares 2 values used for sorting library search fields.
 * Options for the Library search field drop down need a special sorting criteria because any
 * needs to appear at the top.
 */
function libraryOptionsSorter(a, b) {
  const labelA = a.label.toUpperCase(); // ignore upper and lowercase
  const labelB = b.label.toUpperCase(); // ignore upper and lowercase
  // Special cases first because you need any to be the top item.
  if (a.value === 'any') {
    return -1;
  }
  if (b.value === 'any') {
    return 1;
  }
  if (labelA < labelB) {
    return -1;
  }
  if (labelA > labelB) {
    return 1;
  }
  // labels must be equal
  return 0;
}

interface LibrarySearchTabProps {
  formState: Map<string, any>;
  searchInfo: Map<string, any>;
  sources: any;
}

interface LibrarySearchTabState {
  currentTagSearchTerm: string;
  currentCallerSearchTerm: string;
  currentLibrarySearchTerm: string;
}

/**
 * Search tab for the library page.
 */
export default class LibrarySearchTab extends React.Component<
  LibrarySearchTabProps,
  LibrarySearchTabState
> {
  constructor(props) {
    super(props);
    this.state = {
      currentTagSearchTerm: '',
      currentCallerSearchTerm: '',
      currentLibrarySearchTerm: '',
    };
    this.clearSearch = this.clearSearch.bind(this);
    this.handleCallerChange = this.handleCallerChange.bind(this);
    this.handleCallerOpenChange = this.handleCallerOpenChange.bind(this);
    this.handleCallerSearchTermChange = this.handleCallerSearchTermChange.bind(this);
    this.handleCurrentLibrarySearchTermChange =
      this.handleCurrentLibrarySearchTermChange.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.removeCaller = this.removeCaller.bind(this);
  }

  getLibrariesOptions() {
    const libs = this.props.sources.get('libraries');
    const options = [{label: 'Any', value: 'any'}];
    const libOpts = libs.map(function (lib) {
      return {label: lib.name, value: lib.id};
    });
    return options.concat(libOpts);
  }

  getTagsOptions() {
    const tags = this.props.sources.get('tags');
    const selectedTags = this.props.formState.get('tags');

    return (tags || [])
      .filter(tag => {
        return !selectedTags.includes(tag);
      })
      .map(value => {
        return {label: value, value: value};
      });
  }

  handleSearch() {
    LibraryActions.updateLibrarySearchType(ConstValues.librarySearchType.search);
    LibraryActions.searchCalls();
  }

  callerValues(): any[] {
    let callers = this.props.formState.get('callers');
    if (callers === null) {
      callers = [];
    }
    return callers;
  }

  handleCurrentLibrarySearchTermChange(search) {
    this.setState({currentLibrarySearchTerm: search});
  }

  getCallersOptions() {
    return this.props.formState.get('callersAutocomplete').filter(caller => {
      return !this.props.formState.get('callers').some(currentCaller => {
        return caller.value === currentCaller.value;
      });
    });
  }

  handleCallerOpenChange(open) {
    if (!open) {
      LibraryActions.clearCallersAutocomplete();
    }
  }

  handleCallerChange(caller) {
    const callers = this.callerValues();
    callers.push(caller);
    LibraryActions.updateCurrentCallers(callers);
  }

  handleCallerSearchTermChange(term) {
    this.setState({currentCallerSearchTerm: term});
    LibraryActions.getCallersAutocomplete(term, this.props.formState.get('library'));
  }

  currentCallers() {
    const callers: any[] | undefined = this.props.formState.get('callers');
    if (!callers) return null;

    return callers.map(caller => (caller?.label ? caller.label : caller));
  }

  removeCaller(caller) {
    let callers = List(this.callerValues());
    callers = callers.filterNot(item => item.label === caller);
    LibraryActions.updateCurrentCallers(callers.toArray());
  }

  clearSearch() {
    LibraryActions.clearSearchInfo();
    LibraryActions.resetSearchCriteria();
  }

  searchInfoIsNotDefault(searchInfo) {
    return (
      searchInfo &&
      (!!searchInfo.library ||
        (searchInfo.callers && searchInfo.callers.length > 0) ||
        (searchInfo.tags && searchInfo.tags.length > 0))
    );
  }

  render() {
    const libs = this.props.sources.get('libraries');
    const libsAreNotEmpty = libs !== null && libs.length > 0;
    const library = this.props.formState.get('library');
    return (
      <Card className="search plain-border">
        <div className="pane">
          <h2>Search Library</h2>
          <span style={{marginLeft: 20}}>Find calls</span>
        </div>

        <div className="fields">
          {(() => {
            if (libsAreNotEmpty) {
              return (
                <div className="search-field">
                  <div className="label">Library:</div>
                  <SelectField
                    name="library_id"
                    hideResetButton={true}
                    tether={true}
                    className="library-form-select"
                    options={this.getLibrariesOptions().sort(libraryOptionsSorter)}
                    value={library}
                    onChange={LibraryActions.updateLibraryDropdown}
                    onSearchChange={this.handleCurrentLibrarySearchTermChange}
                    updateSearch={this.handleCurrentLibrarySearchTermChange}
                    search={this.state.currentLibrarySearchTerm}
                  />
                </div>
              );
            }
          })()}

          <MultipleSelectField
            selectField={{
              label: {className: 'label multi-field-label', label: 'Caller:'},
              name: 'callerName',
              className: 'library-form-select',
              placeholder: 'Type for suggestions...',
              noResultsMessage: 'No callers matching your terms',
              handleOpenChange: this.handleCallerOpenChange,
              options: this.getCallersOptions(),
              onChange: this.handleCallerChange,
              onSearchChange: this.handleCallerSearchTermChange,
              search: this.state.currentCallerSearchTerm,
              value: this.props.formState.get('caller'),
            }}
            selectedList={{
              containerClassname: 'tag-list-container',
              listClassname: 'tag-list',
              spanClassname: 'span-tag',
              items: this.currentCallers(),
            }}
            removeItem={this.removeCaller}
          />

          <MultipleSelectField
            selectField={{
              label: {
                className: 'label multi-field-label',
                label: 'Tagged with:',
              },
              name: 'tag_id',
              className: 'library-form-select',
              placeholder: 'Type to search tags',
              noResultsMessage: 'No tags matching your terms',
              options: this.getTagsOptions(),
              onChange: LibraryActions.addTag,
              onSearchChange: search => this.setState({currentTagSearchTerm: search}),
              search: this.state.currentTagSearchTerm,
              value: this.props.formState.get('tag'),
            }}
            selectedList={{
              containerClassname: 'tag-list-container',
              listClassname: 'tag-list',
              spanClassname: 'span-tag',
              items: this.props.formState.get('tags'),
            }}
            removeItem={LibraryActions.removeTag}
          />

          <div className="search-field">
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleSearch}
              className="search-button">
              Search Calls
            </Button>
            {this.searchInfoIsNotDefault(this.props.searchInfo) ? (
              <div className="clear-search" onClick={this.clearSearch}>
                Clear Search
              </div>
            ) : null}
          </div>
        </div>
      </Card>
    );
  }
}
